import ToggleSwitch from '@/components/ToggleSwitch';
import { clampBodyText } from '@/features/menu/utils/option-limits-text';
import { planClampForRemoval } from '@/features/menu/utils/option-limits';
import {
  useCreatePriceList,
  useDeleteOptionChoice,
  useDeletePriceListEntry,
  useDeleteProduct,
  useDeleteProductOption,
  useListPriceLists,
  useUpdateProductOption,
  useProduct,
  useReorderProductOptions,
  useUpdateOptionChoice,
  useUpdateProduct,
  useUpsertPriceListEntry,
} from '@/features/menu/hooks/useMenu';
import { loc } from '@/utils/localized';
import { ALLERGENS, ALLERGEN_SET, LABELS } from '@/features/menu/constants';
import { fail, leaveBack, router } from '@/features/menu/utils/product-page-actions';
import ChannelPriceSheetContent, {
  type ClearFn,
} from '@/features/menu/components/ChannelPriceSheetContent';
import AllergenCodesEditContent from '@/features/menu/components/AllergenCodesEditContent';
import CodesSheetContent from '@/features/menu/components/CodesSheetContent';
import CreateProductForm from '@/features/menu/components/CreateProductForm';
import { EditorLoadingBody, EditorNavbar } from '@/features/menu/components/EditorNavbar';
import OptionEditContent from '@/features/menu/components/OptionEditContent';
import PriceSheetContent, { type PriceSubmit } from '@/features/menu/components/PriceSheetContent';
import ProductDetailView from '@/features/menu/components/ProductDetailView';
import { useCanUseAi } from '@/features/ai-assistant/hooks/useCanUseAi';
import { AiDock } from '@/features/ai-changeset/components/AiDock';
import { useProductQuickEdit } from '@/features/menu/hooks/useProductQuickEdit';
import { useT } from '@/i18n';
import { formatMoney, inputToMinor, moneyToInput } from '@/utils/money';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useAllergenTable } from '@/features/allergen-labeling/hooks/useAllergenLabeling';
import type { MenuChannel, Product } from '@restaurant/api-contracts';
import { Allergen, ProductLabel } from '@restaurant/types';
import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Block,
  BlockFooter,
  BlockTitle,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  Preloader,
  f7,
} from 'framework7-react';

interface Props {
  f7route: { params: { menuId: string; productId: string }; query?: { categoryId?: string } };
}

// ─── Pricing section ─────────────────────────────────────────────────────────

// ─── Per-channel pricing ─────────────────────────────────────────────────────

const CHANNELS: { key: MenuChannel; label: string }[] = [
  { key: 'DINE_IN', label: 'Dine-in' },
  { key: 'PICKUP', label: 'Pickup' },
  { key: 'DELIVERY', label: 'Delivery' },
];
// The base price *is* the dine-in price; pickup and delivery are the overridable
// channels on the pricing page.
const channelLabel = (c: MenuChannel) => CHANNELS.find((x) => x.key === c)?.label ?? c;

type PriceLists = ReturnType<typeof useListPriceLists>['data'];

/** The non-default list that governs a channel (oldest wins, mirroring the API). */
function listForChannel(lists: PriceLists, channel: MenuChannel) {
  return (lists ?? []).find((pl) => !pl.isDefault && pl.appliesToChannels.includes(channel));
}
function overrideFor(lists: PriceLists, channel: MenuChannel, productId: string): number | null {
  const entry = listForChannel(lists, channel)?.entries.find((e) => e.productId === productId);
  return entry?.price ?? null;
}

// ─── Slide-in editor pages ───────────────────────────────────────────────────
// Each product editor is a pushed page (slides in from the right) with a Save
// link in the navbar, rather than a bottom sheet. They reuse the same *Content
// form bodies as the inline sheets and pull their data from the cached product /
// price-list queries by id, so navigating in carries no state of its own and a
// Save just navigates back.

/** Pushed "Pricing" page: dine-in (base), pickup and delivery prices edited
 *  inline on one screen and saved together from the navbar. Pickup/delivery left
 *  blank fall back to the dine-in default (the placeholder shows that amount);
 *  typing an amount creates/updates the channel override, clearing it removes it. */
export function PricingOverviewPage({
  f7route,
}: {
  f7route: { params: { menuId: string; productId: string } };
}) {
  const { productId } = f7route.params;
  const { data: product } = useProduct(productId);
  const { data: priceLists } = useListPriceLists();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const t = useT();

  const updateProduct = useUpdateProduct();
  const createList = useCreatePriceList();
  const upsertEntry = useUpsertPriceListEntry();
  const deleteEntry = useDeletePriceListEntry();

  const basePrice = product?.price ?? 0;
  const pickupOverride = product ? overrideFor(priceLists, 'PICKUP', product.id) : null;
  const deliveryOverride = product ? overrideFor(priceLists, 'DELIVERY', product.id) : null;

  // Draft seeded from the product + its channel overrides. A blank channel means
  // "use the default" (placeholder shows the base). The pristine object re-seeds
  // on load and after a save refetch, which resets the dirty state.
  const pristine = useMemo(
    () => ({
      dineIn: moneyToInput(basePrice, currency),
      pickup: pickupOverride != null ? moneyToInput(pickupOverride, currency) : '',
      delivery: deliveryOverride != null ? moneyToInput(deliveryOverride, currency) : '',
    }),
    [basePrice, pickupOverride, deliveryOverride, currency],
  );
  const [draft, setDraft] = useState(pristine);
  useEffect(() => setDraft(pristine), [pristine]);

  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(pristine);
  useUnsavedGuard(dirty);

  const handleSave = async () => {
    if (!product || !priceLists || saving) return;
    setSaving(true);
    try {
      const nextBase = inputToMinor(draft.dineIn, currency);
      if (nextBase !== product.price) {
        await updateProduct.mutateAsync({ productId: product.id, body: { price: nextBase } });
      }
      for (const channel of ['PICKUP', 'DELIVERY'] as MenuChannel[]) {
        const raw = channel === 'PICKUP' ? draft.pickup : draft.delivery;
        const existing = listForChannel(priceLists, channel);
        const entry = existing?.entries.find((e) => e.productId === product.id);
        if (raw.trim() === '') {
          // Back to default - drop the override if there is one.
          if (entry) await deleteEntry.mutateAsync(entry.id);
        } else {
          // Lazily create the channel list the first time it's priced, then upsert.
          const listId =
            existing?.id ??
            (
              await createList.mutateAsync({
                name: channelLabel(channel),
                appliesToChannels: [channel],
              })
            ).id;
          await upsertEntry.mutateAsync({
            priceListId: listId,
            body: { productId: product.id, price: inputToMinor(raw, currency) },
          });
        }
      }
      f7.toast.show({ text: t.menu.pricesSaved, closeTimeout: 2000, position: 'bottom' });
      leaveBack();
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };

  const onField =
    (key: 'dineIn' | 'pickup' | 'delivery') => (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft((d) => ({ ...d, [key]: e.target.value }));

  return (
    <Page>
      <Navbar title={t.menu.pricing} backLink={t.back}>
        <NavRight>
          <F7Link
            onClick={handleSave}
            style={!dirty || saving ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
          >
            {t.saveShort}
          </F7Link>
        </NavRight>
      </Navbar>
      {product && priceLists ? (
        <>
          <BlockTitle>{t.menu.productForm.channelPrices}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListInput
              label={t.menu.productForm.dineIn}
              type="number"
              inputmode="decimal"
              value={draft.dineIn}
              onInput={onField('dineIn')}
            />
            <ListInput
              label={t.menu.productForm.pickup}
              type="number"
              inputmode="decimal"
              placeholder={moneyToInput(basePrice, currency)}
              value={draft.pickup}
              onInput={onField('pickup')}
            />
            <ListInput
              label={t.menu.productForm.delivery}
              type="number"
              inputmode="decimal"
              placeholder={moneyToInput(basePrice, currency)}
              value={draft.delivery}
              onInput={onField('delivery')}
            />
          </List>
          <BlockFooter>{t.menu.channelPricesFooter}</BlockFooter>
        </>
      ) : (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
    </Page>
  );
}

export function BasePriceEditPage({
  f7route,
}: {
  f7route: { params: { menuId: string; productId: string } };
}) {
  const t = useT();
  const { productId } = f7route.params;
  const { data: product } = useProduct(productId);
  const updateProduct = useUpdateProduct();
  const submitRef = useRef<() => void>(() => {});
  return (
    <Page pageContent={false}>
      <EditorNavbar title={t.menu.basePrice} submitRef={submitRef} />
      {product ? (
        <PriceSheetContent
          open
          onClose={leaveBack}
          initial={product.price}
          onSubmit={(price, done, err) => {
            updateProduct.mutate({ productId, body: { price } }, { onSuccess: done, onError: err });
          }}
          submitRef={submitRef}
        />
      ) : (
        <EditorLoadingBody />
      )}
    </Page>
  );
}

export function ChannelPriceEditPage({
  f7route,
}: {
  f7route: { params: { menuId: string; productId: string; channel: string } };
}) {
  const t = useT();
  const { productId, channel: channelParam } = f7route.params;
  const channel = channelParam.toUpperCase() as MenuChannel;
  const { data: product } = useProduct(productId);
  const { data: priceLists } = useListPriceLists();
  const createList = useCreatePriceList();
  const upsertEntry = useUpsertPriceListEntry();
  const deleteEntry = useDeletePriceListEntry();
  const submitRef = useRef<() => void>(() => {});

  // Localized channel name for display only - the price-list `name` sent to the
  // API below intentionally keeps the static `channelLabel` (data, not UI text).
  const channelDisplayName =
    channel === 'PICKUP'
      ? t.menu.productForm.pickup
      : channel === 'DELIVERY'
        ? t.menu.productForm.delivery
        : t.menu.productForm.dineIn;

  return (
    <Page pageContent={false}>
      <EditorNavbar
        title={t.menu.channelPriceEditTitle(channelDisplayName)}
        submitRef={submitRef}
      />
      {product ? (
        (() => {
          const override = overrideFor(priceLists, channel, product.id);

          // Lazily create the channel's price list the first time it's priced,
          // then upsert this product's override into it.
          const saveChannel: PriceSubmit = async (price, done, errOut) => {
            try {
              const existing = listForChannel(priceLists, channel);
              const listId =
                existing?.id ??
                (
                  await createList.mutateAsync({
                    name: channelLabel(channel),
                    appliesToChannels: [channel],
                  })
                ).id;
              await upsertEntry.mutateAsync({
                priceListId: listId,
                body: { productId: product.id, price },
              });
              done();
            } catch (e) {
              errOut(e);
            }
          };

          const clearChannel: ClearFn = async (done, errOut) => {
            try {
              const entry = listForChannel(priceLists, channel)?.entries.find(
                (e) => e.productId === product.id,
              );
              if (entry) await deleteEntry.mutateAsync(entry.id);
              done();
            } catch (e) {
              errOut(e);
            }
          };

          return (
            <ChannelPriceSheetContent
              open
              onClose={leaveBack}
              base={product.price}
              initial={override ?? product.price}
              hasOverride={override != null}
              onSubmit={saveChannel}
              onClear={clearChannel}
              submitRef={submitRef}
            />
          );
        })()
      ) : (
        <EditorLoadingBody />
      )}
    </Page>
  );
}

export function OptionEditPage({
  f7route,
}: {
  f7route: { params: { menuId: string; productId: string; optionId: string } };
}) {
  const t = useT();
  const { menuId, productId, optionId } = f7route.params;
  const { data: product } = useProduct(productId);
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const deleteOption = useDeleteProductOption();

  const option = product?.options.find((o) => o.id === optionId) ?? null;
  const ready = !!product && !!option;

  const onDelete = () => {
    if (!option) return;
    f7.dialog.confirm(t.menu.deleteOptionConfirm(loc(option.name)), t.menu.deleteOption, () => {
      deleteOption.mutate({ optionId: option.id }, { onSuccess: leaveBack, onError: fail });
    });
  };

  return (
    <Page>
      <Navbar title={t.menu.editOption} backLink={t.back}>
        {ready && (
          <NavRight>
            <F7Link
              iconF7="trash"
              aria-label={t.menu.deleteOption}
              onClick={onDelete}
              style={{ color: 'var(--f7-color-red)' }}
            />
          </NavRight>
        )}
      </Navbar>
      {ready && product && option ? (
        <OptionEditContent
          key={option.id}
          product={product}
          option={option}
          menuId={menuId}
          currency={currency}
        />
      ) : (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
    </Page>
  );
}

export function ChoiceEditPage({
  f7route,
}: {
  f7route: {
    params: { menuId: string; productId: string; optionId: string; choiceId: string };
  };
}) {
  const t = useT();
  const { productId, optionId, choiceId } = f7route.params;
  const { data: product } = useProduct(productId);
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const updateChoice = useUpdateOptionChoice();
  const deleteChoice = useDeleteOptionChoice();
  const updateOption = useUpdateProductOption();

  const option = product?.options.find((o) => o.id === optionId);
  const choice = option?.choices.find((c) => c.id === choiceId);

  const editName = () => {
    if (!choice) return;
    f7.dialog.prompt(
      t.menu.choiceName,
      t.menu.productForm.editName,
      (value) => {
        const text = value.trim();
        if (!text) return;
        updateChoice.mutate(
          { choiceId: choice.id, body: { name: { en: text } } },
          { onError: fail },
        );
      },
      undefined,
      loc(choice.name),
    );
  };

  const editPrice = () => {
    if (!choice) return;
    f7.dialog.prompt(
      t.menu.extraPrice,
      t.menu.extraPrice,
      (value) => {
        const text = value.trim();
        if (text === '') return;
        updateChoice.mutate(
          { choiceId: choice.id, body: { price: inputToMinor(text, currency) } },
          { onError: fail },
        );
      },
      undefined,
      moneyToInput(choice.price, currency),
    );
  };

  const onDelete = () => {
    if (!choice || !option) return;
    const removeChoice = () =>
      deleteChoice.mutate({ choiceId: choice.id }, { onSuccess: leaveBack, onError: fail });
    const plan = planClampForRemoval(option, choice);
    if (!plan) {
      f7.dialog.confirm(
        t.menu.deleteChoiceConfirm(loc(choice.name)),
        t.menu.deleteChoice,
        removeChoice,
      );
      return;
    }
    const lt = t.menu.optionLimits;
    const body = {
      ...plan.limits,
      ...(plan.isRequired === null ? {} : { isRequired: plan.isRequired }),
    };
    f7.dialog
      .create({
        title: lt.clampTitle,
        text: clampBodyText(plan, loc(option.name), loc(choice.name), lt),
        buttons: [
          { text: t.cancel },
          {
            text: lt.lowerAndDelete(plan.n),
            onClick: () =>
              updateOption.mutate(
                { optionId: option.id, body },
                { onSuccess: removeChoice, onError: fail },
              ),
          },
        ],
      })
      .open();
  };

  return (
    <Page>
      <Navbar title={t.menu.editChoice} backLink={t.back}>
        {choice && (
          <NavRight>
            <F7Link
              iconF7="trash"
              aria-label={t.menu.deleteChoice}
              onClick={onDelete}
              style={{ color: 'var(--f7-color-red)' }}
            />
          </NavRight>
        )}
      </Navbar>
      {choice ? (
        <>
          <BlockTitle>{t.menu.productForm.general}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem
              title={t.menu.choiceName}
              after={loc(choice.name)}
              link="#"
              onClick={editName}
            />
            <ListItem
              title={t.menu.extraPrice}
              after={formatMoney(choice.price, currency)}
              link="#"
              onClick={editPrice}
            />
          </List>

          <BlockTitle>{t.menu.productForm.allergens}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem
              title={t.menu.productForm.allergens}
              after={choice.allergens.length > 0 ? `${choice.allergens.length}` : '-'}
              link={`/menu/${f7route.params.menuId}/product/${productId}/option/${optionId}/choice/${choiceId}/allergens`}
            />
          </List>

          <BlockTitle>{t.menu.productForm.availability}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem title={t.menu.productForm.inStock}>
              <ToggleSwitch
                checked={choice.inStock}
                onChange={(v) =>
                  updateChoice.mutate(
                    { choiceId: choice.id, body: { inStock: v } },
                    { onError: fail },
                  )
                }
              />
            </ListItem>
          </List>
          <BlockFooter>{t.menu.productForm.outOfStockChoicesFooter}</BlockFooter>
        </>
      ) : (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
    </Page>
  );
}

export function AllergensEditPage({
  f7route,
}: {
  f7route: { params: { menuId: string; productId: string } };
}) {
  const t = useT();
  const { productId } = f7route.params;
  const { data: product } = useProduct(productId);
  const updateProduct = useUpdateProduct();
  const submitRef = useRef<() => void>(() => {});
  // Memoized so its identity is stable across taps (the form re-syncs from it).
  const initial = useMemo(
    () => product?.allergens.filter((a): a is Allergen => ALLERGEN_SET.has(a)) ?? [],
    [product],
  );
  return (
    <Page pageContent={false}>
      <EditorNavbar title={t.menu.productForm.allergens} submitRef={submitRef} />
      {product ? (
        <CodesSheetContent
          open
          onClose={leaveBack}
          all={ALLERGENS}
          initial={initial}
          onSubmit={(codes, done, err) => {
            updateProduct.mutate(
              { productId, body: { allergens: codes } },
              { onSuccess: done, onError: err },
            );
          }}
          submitRef={submitRef}
        />
      ) : (
        <EditorLoadingBody />
      )}
    </Page>
  );
}

export function ChoiceAllergensEditPage({
  f7route,
}: {
  f7route: {
    params: { menuId: string; productId: string; optionId: string; choiceId: string };
  };
}) {
  const t = useT();
  const { productId, optionId, choiceId } = f7route.params;
  const { data: product } = useProduct(productId);
  const { data: table } = useAllergenTable();
  const updateChoice = useUpdateOptionChoice();
  const submitRef = useRef<() => void>(() => {});

  const choice = product?.options
    .find((o) => o.id === optionId)
    ?.choices.find((c) => c.id === choiceId);
  const initial = useMemo(() => choice?.allergenCodes ?? [], [choice]);

  return (
    <Page pageContent={false}>
      <EditorNavbar title={t.menu.productForm.allergens} submitRef={submitRef} />
      {choice && table ? (
        <AllergenCodesEditContent
          open
          onClose={leaveBack}
          table={table}
          initial={initial}
          onSubmit={(codes, done, err) => {
            updateChoice.mutate(
              { choiceId, body: { allergenCodes: codes } },
              { onSuccess: done, onError: err },
            );
          }}
          submitRef={submitRef}
        />
      ) : (
        <EditorLoadingBody />
      )}
    </Page>
  );
}

export function AllergenCodesEditPage({
  f7route,
}: {
  f7route: { params: { menuId: string; productId: string } };
}) {
  const t = useT();
  const { productId } = f7route.params;
  const { data: product } = useProduct(productId);
  const { data: table } = useAllergenTable();
  const updateProduct = useUpdateProduct();
  const submitRef = useRef<() => void>(() => {});
  const initial = useMemo(() => product?.allergenCodes ?? [], [product]);
  return (
    <Page pageContent={false}>
      <EditorNavbar title={t.menu.productForm.allergens} submitRef={submitRef} />
      {product && table ? (
        <AllergenCodesEditContent
          open
          onClose={leaveBack}
          table={table}
          initial={initial}
          onSubmit={(codes, done, err) => {
            updateProduct.mutate(
              { productId, body: { allergenCodes: codes } },
              { onSuccess: done, onError: err },
            );
          }}
          submitRef={submitRef}
        />
      ) : (
        <EditorLoadingBody />
      )}
    </Page>
  );
}

export function LabelsEditPage({
  f7route,
}: {
  f7route: { params: { menuId: string; productId: string } };
}) {
  const t = useT();
  const { productId } = f7route.params;
  const { data: product } = useProduct(productId);
  const updateProduct = useUpdateProduct();
  const submitRef = useRef<() => void>(() => {});
  const initial = useMemo(
    () => product?.labels.filter((l): l is ProductLabel => (LABELS as string[]).includes(l)) ?? [],
    [product],
  );
  return (
    <Page pageContent={false}>
      <EditorNavbar title={t.menu.productForm.labels} submitRef={submitRef} />
      {product ? (
        <CodesSheetContent
          open
          onClose={leaveBack}
          all={LABELS}
          initial={initial}
          onSubmit={(codes, done, err) => {
            updateProduct.mutate(
              { productId, body: { labels: codes } },
              { onSuccess: done, onError: err },
            );
          }}
          submitRef={submitRef}
        />
      ) : (
        <EditorLoadingBody />
      )}
    </Page>
  );
}

function OptionOrderList({ product }: { product: Product }) {
  const t = useT();
  const reorderOptions = useReorderProductOptions();
  const options = product.options
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));

  const handleSort = (from: number, to: number) => {
    if (from === to) return;
    const next = options.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    reorderOptions.mutate({ productId: product.id, orderedIds: next.map((o) => o.id) });
  };

  return (
    <>
      <BlockTitle>{t.menu.dragToReorderOptions}</BlockTitle>
      <List
        strongIos
        insetIos
        dividersIos
        sortable
        sortableEnabled
        onSortableSort={(sortData: { from: number; to: number }) =>
          handleSort(sortData.from, sortData.to)
        }
      >
        {options.map((o) => (
          <ListItem key={o.id} title={loc(o.name)} />
        ))}
      </List>
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function ProductPage({ f7route }: Props) {
  const t = useT();
  const { menuId, productId } = f7route.params;
  const categoryId = f7route.query?.categoryId;
  const isNew = productId === 'new';
  const { data: product, isLoading } = useProduct(isNew ? null : productId);
  const deleteProduct = useDeleteProduct();
  const canUseAi = useCanUseAi();
  const quickEdit = useProductQuickEdit(menuId, productId, isNew ? undefined : product);
  const { draft } = quickEdit;
  const [reorderMode, setReorderMode] = useState(false);
  const canReorderOptions = (product?.options.length ?? 0) > 1;

  useEffect(() => setReorderMode(false), [productId]);
  useEffect(() => {
    if (!canReorderOptions) setReorderMode(false);
  }, [canReorderOptions]);

  const openActions = (e: ReactMouseEvent) => {
    if (!product) return;
    // Anchor to the tapped ellipsis so the wide-viewport popover conversion has a
    // backdrop and dismisses on outside click. Defer the destructive confirm to
    // `closed` - opening a dialog from an action-sheet button races Framework7's
    // shared modal backdrop (the confirm flashes and its OK never fires).
    const targetEl = e.currentTarget as HTMLElement;
    let picked: (() => void) | null = null;
    f7.actions
      .create({
        targetEl,
        buttons: [
          [
            { text: loc(product.name), label: true },
            ...(product.options.length > 1
              ? [
                  {
                    text: t.menu.reorderOptions,
                    onClick: () => {
                      picked = () => setReorderMode(true);
                    },
                  },
                ]
              : []),
            {
              text: t.menu.deleteProduct,
              color: 'red',
              onClick: () => {
                picked = () =>
                  f7.dialog.confirm(
                    t.menu.deleteProductConfirm(loc(product.name)),
                    t.menu.deleteProduct,
                    () => {
                      deleteProduct.mutate(
                        { productId },
                        { onSuccess: () => router()?.back(), onError: fail },
                      );
                    },
                  );
              },
            },
            { text: t.cancel },
          ],
        ],
        on: {
          closed: () => {
            picked?.();
            picked = null;
          },
        },
      })
      .open();
  };

  return (
    <Page name="product">
      <Navbar
        title={isNew ? t.menu.newProduct : product ? loc(product.name) : t.menu.product}
        backLink={t.back}
      >
        <NavRight>
          {reorderMode ? (
            <F7Link onClick={() => setReorderMode(false)}>{t.menu.doneReordering}</F7Link>
          ) : (
            !isNew &&
            product && <F7Link iconF7="ellipsis" onClick={(e: ReactMouseEvent) => openActions(e)} />
          )}
        </NavRight>
      </Navbar>
      {canUseAi && product && draft && !reorderMode && (
        <AiDock
          dock={quickEdit}
          testId="product-ai-dock"
          title={t.aiDock.productTitle}
          placeholder={t.menuQuickEdit.productPlaceholder}
        />
      )}
      {isNew && <CreateProductForm menuId={menuId} categoryId={categoryId} />}
      {!isNew && isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {product && reorderMode && <OptionOrderList product={product} />}
      {product && draft && !reorderMode && (
        <>
          <ProductDetailView
            product={product}
            menuId={menuId}
            view={draft.view}
            store={quickEdit.navigation.store}
          />
        </>
      )}
      {!isNew && !isLoading && !product && (
        <Block strongIos insetIos>
          <p>{t.menu.productNotFound}</p>
        </Block>
      )}
    </Page>
  );
}

export default ProductPage;
